import React, { useState, useRef, useEffect } from "react";
import axios from "axios";
import { Button } from "../ui/button";
import { Textarea } from "../ui/textarea";
import { Badge } from "../ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { ScrollArea } from "../ui/scroll-area";
import {
  ClipboardPaste, Loader2, Check, X, AlertCircle, Sparkles,
  User, Phone, MapPin, Briefcase, DollarSign, Wrench, Users, Building2,
  Wand2, FileText, ClipboardCheck, CalendarDays,
} from "lucide-react";
import { API } from "../../App";
import { toast } from "sonner";

const SAMPLE_FORMAT = `American Services
Job: 8XMQ3
Name: Jennifer
Phone1: +19412274013
Job Type: Bedroom Lockout
Price: N/A
Date: 10/21/2026
Time: between 2-4 PM
Address: 7912 Jacinto Road
Elk Grove, CA 95758

T: $600
P: $25
Tech: John`;

// Small chips shown above the textarea — visual hint of what the parser
// extracts. Kept compact so they double as a "what does this do?" affordance
// without the user needing to read instructions.
const RECOGNITION_CHIPS = [
  { icon: User, label: "Customer" },
  { icon: Phone, label: "Phone" },
  { icon: MapPin, label: "Address" },
  { icon: CalendarDays, label: "Date & Time" },
  { icon: Briefcase, label: "Job Type" },
  { icon: DollarSign, label: "Total" },
  { icon: Wrench, label: "Parts" },
  { icon: Users, label: "Tech" },
  { icon: Building2, label: "Source" },
];

const PasteTicketModal = ({ open, onOpenChange, onTicketParsed }) => {
  const [ticketText, setTicketText] = useState("");
  const [parsing, setParsing] = useState(false);
  const [parsedData, setParsedData] = useState(null);
  const [showSample, setShowSample] = useState(false);
  const [pasteFlash, setPasteFlash] = useState(false);
  const textareaRef = useRef(null);

  // Auto-focus the textarea on open so paste is one-keystroke away.
  useEffect(() => {
    if (open && !parsedData) {
      const t = setTimeout(() => textareaRef.current?.focus(), 100);
      return () => clearTimeout(t);
    }
  }, [open, parsedData]);

  const handleParse = async () => {
    if (!ticketText.trim()) {
      toast.error("Please paste a ticket first");
      return;
    }
    setParsing(true);
    setParsedData(null);
    try {
      const token = localStorage.getItem("token");
      const res = await axios.post(`${API}/jobs/parse-ticket`,
        { ticket_text: ticketText },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setParsedData(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to parse ticket");
    } finally {
      setParsing(false);
    }
  };

  // One-click clipboard import — saves the user from focusing the textarea
  // first then Ctrl/Cmd+V. Falls back gracefully if the browser blocks
  // clipboard access (Safari without HTTPS, etc).
  const handlePasteFromClipboard = async () => {
    try {
      const text = await navigator.clipboard.readText();
      if (!text.trim()) {
        toast.info("Clipboard is empty");
        return;
      }
      setTicketText(text);
      setPasteFlash(true);
      setTimeout(() => setPasteFlash(false), 1200);
      textareaRef.current?.focus();
    } catch {
      toast.error("Couldn't read clipboard — paste manually with ⌘V / Ctrl+V");
    }
  };

  // Loads the demo ticket into the textarea so a new user can see exactly
  // what gets extracted without dispatching a real ticket.
  const handleTrySample = () => {
    setTicketText(SAMPLE_FORMAT);
    setShowSample(false);
    setPasteFlash(true);
    setTimeout(() => setPasteFlash(false), 1200);
    textareaRef.current?.focus();
  };

  const handleCreateJob = () => {
    if (!parsedData) return;
    onTicketParsed(parsedData);
    handleClose();
  };

  const handleClose = () => {
    setTicketText("");
    setParsedData(null);
    setShowSample(false);
    onOpenChange(false);
  };

  const lineCount = ticketText ? ticketText.split("\n").length : 0;
  const charCount = ticketText.length;

  const MatchBadge = ({ matched, created }) => (
    <Badge variant="outline" className={`text-[10px] px-1.5 py-0 ${created
      ? "bg-blue-500/15 text-blue-400 border-blue-500/30"
      : matched
        ? "bg-emerald-500/15 text-emerald-400 border-emerald-500/30"
        : "bg-amber-500/15 text-amber-400 border-amber-500/30"}`}>
      {created ? "Auto-created" : matched ? "Matched" : "New"}
    </Badge>
  );

  const FieldRow = ({ icon: Icon, label, value, matched, created, matchLabel }) => {
    if (!value && value !== 0) return null;
    return (
      <div className="flex items-start gap-3 py-2">
        <div className="w-7 h-7 rounded-lg bg-muted/40 flex items-center justify-center shrink-0">
          <Icon className="w-3.5 h-3.5 text-muted-foreground" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground">{label}</span>
            {matched !== undefined && <MatchBadge matched={matched} created={created} />}
          </div>
          <p className="text-sm text-foreground font-medium truncate">
            {typeof value === "number" ? `$${value.toFixed(2)}` : value}
          </p>
          {matchLabel && (
            <p className="text-xs text-muted-foreground/70 mt-0.5 truncate">
              Linked to: {matchLabel}
            </p>
          )}
        </div>
      </div>
    );
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent
        className="bg-background border-border w-[95vw] max-w-xl max-h-[90vh] p-0 flex flex-col overflow-hidden"
        data-testid="paste-ticket-modal"
      >
        {/* Header — title + AI subtitle so the new capability is discoverable
            without users having to "find out the hard way" by pasting a
            non-standard ticket. */}
        <DialogHeader className="p-4 sm:p-5 pb-3 flex-shrink-0 bg-gradient-to-b from-[#FFCC00]/[0.04] to-transparent">
          <DialogTitle className="text-foreground font-['Manrope'] flex items-center gap-2 text-lg">
            <div className="w-8 h-8 rounded-lg bg-[#FFCC00]/15 flex items-center justify-center">
              <ClipboardPaste className="w-4 h-4 text-[#FFCC00]" />
            </div>
            Paste Closing Ticket
          </DialogTitle>
          <p className="text-xs text-muted-foreground flex items-center gap-1.5 pl-10 -mt-1">
            <Wand2 className="w-3 h-3 text-[#FFCC00]" />
            <span>
              Powered by AI — paste any format, even free-form notes
            </span>
          </p>
        </DialogHeader>

        <ScrollArea className="flex-1 overflow-auto">
          <div className="p-4 sm:p-5 pt-2 space-y-4">
            {/* Paste Area */}
            {!parsedData && (
              <>
                {/* Recognition chips — visual confirmation of what the
                    parser extracts. Doubles as a "you don't have to label
                    these, AI figures it out" hint. */}
                <div className="space-y-2">
                  <p className="text-[10px] uppercase tracking-wider text-muted-foreground/70 font-medium">
                    Auto-detects
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {RECOGNITION_CHIPS.map(({ icon: Icon, label }) => (
                      <span
                        key={label}
                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-muted/40 border border-border/60 text-[11px] text-muted-foreground"
                        data-testid={`recognition-chip-${label.toLowerCase()}`}
                      >
                        <Icon className="w-3 h-3" />
                        {label}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Action row — quick paste + sample buttons + sample toggle */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <label className="text-sm font-medium text-foreground">
                      Ticket Text
                    </label>
                    <div className="flex items-center gap-1">
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={handlePasteFromClipboard}
                        className="h-7 px-2 text-xs text-muted-foreground hover:text-foreground"
                        data-testid="paste-from-clipboard-btn"
                      >
                        <ClipboardCheck className="w-3 h-3 mr-1" />
                        Paste from clipboard
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={handleTrySample}
                        className="h-7 px-2 text-xs text-[#FFCC00] hover:text-[#FFCC00] hover:bg-[#FFCC00]/10"
                        data-testid="try-sample-btn"
                      >
                        <FileText className="w-3 h-3 mr-1" />
                        Try sample
                      </Button>
                    </div>
                  </div>

                  <div className="relative">
                    <Textarea
                      ref={textareaRef}
                      placeholder={"Paste here — works with any format:\n• Standard “Name: ... / Phone: ... / Job: ...” tickets\n• Forwarded SMS dispatches\n• Free-form notes (AI will figure it out)"}
                      value={ticketText}
                      onChange={(e) => setTicketText(e.target.value)}
                      className={`min-h-[180px] bg-theme-surface border-border text-foreground font-mono text-sm resize-none transition-all duration-300 ${
                        pasteFlash ? "ring-2 ring-[#FFCC00]/40 border-[#FFCC00]/40" : ""
                      }`}
                      data-testid="ticket-text-input"
                    />
                    {/* Bottom-right meta hint — char/line count + AI assist
                        promise. Stays out of the way until user types. */}
                    {ticketText && (
                      <div
                        className="absolute bottom-2 right-3 text-[10px] text-muted-foreground/70 font-mono select-none pointer-events-none flex items-center gap-2"
                        data-testid="ticket-text-meta"
                      >
                        <span>{lineCount} lines · {charCount} chars</span>
                      </div>
                    )}
                  </div>

                  {/* Sample format reveal — collapsible to save vertical space */}
                  <button
                    type="button"
                    onClick={() => setShowSample(!showSample)}
                    className="text-[11px] text-muted-foreground hover:text-foreground inline-flex items-center gap-1"
                    data-testid="toggle-sample-format"
                  >
                    {showSample ? "Hide" : "Show"} reference format
                  </button>
                  {showSample && (
                    <pre className="text-[11px] text-muted-foreground bg-muted/20 rounded-lg p-3 border border-border/60 whitespace-pre-wrap font-mono leading-relaxed">
                      {SAMPLE_FORMAT}
                    </pre>
                  )}
                </div>
              </>
            )}

            {/* Parsed Results */}
            {parsedData && (
              <div className="space-y-3">
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <div className="flex items-center gap-2">
                    <div className="w-7 h-7 rounded-lg bg-emerald-500/15 flex items-center justify-center">
                      <Check className="w-4 h-4 text-emerald-400" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground">Ticket Parsed</p>
                      <p className="text-[11px] text-muted-foreground">
                        Review fields below, then create the job
                      </p>
                    </div>
                  </div>
                  {parsedData._parser === "hybrid" && (
                    <Badge
                      variant="outline"
                      className="bg-gradient-to-r from-[#FFCC00]/15 to-[#FFCC00]/5 text-[#FFCC00] border-[#FFCC00]/40 text-[10px] gap-1 px-2 py-0.5 shadow-[0_0_8px_rgba(255,204,0,0.15)]"
                      data-testid="parse-ai-assisted-badge"
                      title="Some fields were extracted using AI because the ticket format wasn't fully recognized."
                    >
                      <Sparkles className="w-3 h-3" />
                      AI-assisted
                    </Badge>
                  )}
                </div>

                <div className="bg-muted/20 rounded-xl border border-border p-3 divide-y divide-border/50">
                  <FieldRow
                    icon={Building2}
                    label="Job Source"
                    value={parsedData.source_name}
                    matched={!!parsedData.matched_source_id}
                    created={!!parsedData.source_created}
                    matchLabel={parsedData.match_details?.source}
                  />
                  <FieldRow
                    icon={User}
                    label="Customer Name"
                    value={parsedData.customer_name}
                    matched={!!parsedData.matched_customer_id}
                    matchLabel={parsedData.match_details?.customer}
                  />
                  <FieldRow
                    icon={Phone}
                    label="Phone"
                    value={[parsedData.customer_phone, parsedData.customer_phone_ext ? `ext. ${parsedData.customer_phone_ext}` : ""].filter(Boolean).join(" ")}
                  />
                  {parsedData.customer_phone2 && (
                    <FieldRow
                      icon={Phone}
                      label="Phone 2"
                      value={[parsedData.customer_phone2, parsedData.customer_phone2_ext ? `ext. ${parsedData.customer_phone2_ext}` : ""].filter(Boolean).join(" ")}
                    />
                  )}
                  <FieldRow
                    icon={MapPin}
                    label="Address"
                    value={
                      parsedData.address_street
                        ? [parsedData.address_street, parsedData.address_city, [parsedData.address_state, parsedData.zip_code].filter(Boolean).join(" ")].filter(Boolean).join(", ")
                        : parsedData.customer_address
                    }
                  />
                  <FieldRow
                    icon={CalendarDays}
                    label="Scheduled"
                    value={
                      parsedData.scheduled_date || parsedData.scheduled_time
                        ? [
                            parsedData.scheduled_date,
                            parsedData.scheduled_time
                              ? parsedData.scheduled_time_end
                                ? `between ${parsedData.scheduled_time} – ${parsedData.scheduled_time_end}`
                                : parsedData.scheduled_time
                              : "",
                          ].filter(Boolean).join(" · ")
                        : null
                    }
                  />
                  <FieldRow
                    icon={Briefcase}
                    label="Job Type"
                    value={parsedData.job_type}
                    matched={!!parsedData.matched_service_id}
                    created={!!parsedData.service_created}
                    matchLabel={parsedData.match_details?.service}
                  />
                  <FieldRow
                    icon={DollarSign}
                    label="Total Amount"
                    value={parsedData.total_amount}
                  />
                  <FieldRow
                    icon={Wrench}
                    label="Parts Cost"
                    value={parsedData.parts_cost}
                  />
                  <FieldRow
                    icon={Users}
                    label="Technician"
                    value={parsedData.technician_name}
                    matched={!!parsedData.matched_technician_id}
                    matchLabel={parsedData.match_details?.technician}
                  />
                </div>

                {Object.keys(parsedData.match_details || {}).length > 0 && (
                  <p className="text-xs text-muted-foreground flex items-start gap-1.5">
                    <AlertCircle className="w-3 h-3 mt-0.5 shrink-0" />
                    <span>Matched fields will auto-link to existing records. You can change them in the form.</span>
                  </p>
                )}
              </div>
            )}
          </div>
        </ScrollArea>

        {/* Footer — PINNED to the bottom of the modal (outside ScrollArea)
            so the action buttons are ALWAYS visible on mobile, regardless
            of how tall the parsed-fields list is. The previous version had
            these buttons inside ScrollArea which on small viewports pushed
            them below the fold ("only Back button visible" report). Also
            swapped `btn-primary` (designed for landing CTAs — `px-8 py-3
            rounded-full hover:scale-105`) for compact, consistent dialog
            footer styling that respects `flex-1` at narrow widths. */}
        <div
          className="p-3 sm:p-4 border-t border-border bg-background shrink-0"
          data-testid="paste-ticket-footer"
        >
          {!parsedData ? (
            <Button
              onClick={handleParse}
              disabled={parsing || !ticketText.trim()}
              className="w-full h-11 bg-[#FFCC00] hover:bg-[#E6B800] text-black font-semibold rounded-lg shadow-[0_0_12px_rgba(255,204,0,0.25)] transition-colors disabled:opacity-50 disabled:cursor-not-allowed disabled:shadow-none"
              data-testid="parse-ticket-btn"
            >
              {parsing ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Parsing ticket…</>
              ) : (
                <><Sparkles className="w-4 h-4 mr-2" /> Parse Ticket</>
              )}
            </Button>
          ) : (
            <div className="flex gap-2">
              <Button
                variant="outline"
                className="flex-1 h-10 rounded-lg border-border text-foreground hover:bg-theme-hover"
                onClick={() => setParsedData(null)}
                data-testid="back-to-paste-btn"
              >
                <X className="w-4 h-4 mr-1.5" /> Back
              </Button>
              <Button
                className="flex-1 h-10 rounded-lg bg-[#FFCC00] hover:bg-[#E6B800] text-black font-semibold shadow-[0_0_10px_rgba(255,204,0,0.22)] transition-colors"
                onClick={handleCreateJob}
                data-testid="create-from-ticket-btn"
              >
                <Check className="w-4 h-4 mr-1.5" /> Create Job
              </Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PasteTicketModal;
